// batch-62 kostprijs-werkelijk (FO-460 brandstoffacturen + FO-461 loonstrookcomponenten).
//
// Gedeelde enums, labels, request-schema's en DTO's voor de werkelijke kostprijs:
// de MKB-brandstoffactuur regelgewijs gesplitst en gekoppeld aan voertuig + berijder,
// de loonstrookcomponenten via een versie-vaste mapping in kostprijscategorieen,
// bijtelling/eigen bijdrage per medewerker-periode met de voertuigtoerekening, en de
// werkelijke kostprijs per periode naast de norm (EmployeeCostProfile).
//
// Eén bron voor API (`.safeParse`) en backoffice-formulieren (client-side validatie +
// inline veldfouten). Bedragen in DTO's zijn euro's als getal (de DB is Decimal).

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Periodes ────────────────────────────────────────────────────────────────

/** Periode-label `jjjj-mm` (NL-maand). */
export const PERIODE_LABEL_RE = /^\d{4}-(0[1-9]|1[0-2])$/

export const periodeLabelSchema = z
  .string()
  .regex(PERIODE_LABEL_RE, 'Gebruik een periode als jjjj-mm (bijv. 2026-09).')

/** Maximaal aantal maanden in één overzicht/export (begrenst de query). */
export const KOSTPRIJS_MAX_PERIODE_MAANDEN = 24

export const kostprijsPeriodeQuerySchema = z
  .object({ van: periodeLabelSchema, tot: periodeLabelSchema })
  .superRefine((v, ctx) => {
    if (v.van > v.tot) {
      ctx.addIssue({ code: 'custom', path: ['tot'], message: 'De eindperiode ligt vóór de beginperiode.' })
      return
    }
    const [vj, vm] = v.van.split('-').map(Number) as [number, number]
    const [tj, tm] = v.tot.split('-').map(Number) as [number, number]
    const maanden = (tj - vj) * 12 + (tm - vm) + 1
    if (maanden > KOSTPRIJS_MAX_PERIODE_MAANDEN) {
      ctx.addIssue({
        code: 'custom',
        path: ['tot'],
        message: `Kies maximaal ${KOSTPRIJS_MAX_PERIODE_MAANDEN} maanden.`,
      })
    }
  })
export type KostprijsPeriodeQuery = z.infer<typeof kostprijsPeriodeQuerySchema>

// ─── Brandstof (FO-460) ──────────────────────────────────────────────────────

export const BRANDSTOF_KOSTENSOORT = [
  'BRANDSTOF',
  'ADBLUE',
  'WASSEN',
  'TOL',
  'KAARTKOSTEN',
  'TOESLAG',
  'OVERIG',
] as const
export type BrandstofKostensoort = (typeof BRANDSTOF_KOSTENSOORT)[number]

export const BRANDSTOF_KOSTENSOORT_LABELS: Record<BrandstofKostensoort, string> = {
  BRANDSTOF: 'Brandstof',
  ADBLUE: 'AdBlue',
  WASSEN: 'Wassen',
  TOL: 'Tol',
  KAARTKOSTEN: 'Kaartkosten',
  TOESLAG: 'Toeslag',
  OVERIG: 'Overig',
}

export const BRANDSTOF_FACTUUR_STATUS = ['TE_CONTROLEREN', 'VERWERKT', 'VERWORPEN'] as const
export type BrandstofFactuurStatus = (typeof BRANDSTOF_FACTUUR_STATUS)[number]

export const BRANDSTOF_FACTUUR_STATUS_LABELS: Record<BrandstofFactuurStatus, string> = {
  TE_CONTROLEREN: 'Te controleren',
  VERWERKT: 'Verwerkt',
  VERWORPEN: 'Verworpen',
}

export const BRANDSTOF_REGEL_STATUS = ['GEKOPPELD', 'REVIEW', 'ALGEMEEN'] as const
export type BrandstofRegelStatus = (typeof BRANDSTOF_REGEL_STATUS)[number]

export const BRANDSTOF_REGEL_STATUS_LABELS: Record<BrandstofRegelStatus, string> = {
  GEKOPPELD: 'Gekoppeld',
  REVIEW: 'Te koppelen',
  ALGEMEEN: 'Algemeen (niet aan een voertuig toe te rekenen)',
}

export const BRANDSTOF_REVIEW_REDEN = [
  'ONBEKENDE_PAS',
  'PAS_ZONDER_VOERTUIG',
  'VOERTUIG_ZONDER_BERIJDER',
  'BERIJDER_AMBIGU',
  'KENTEKEN_ONBEKEND',
  'GEEN_PAS_OF_KENTEKEN',
  'PAS_KENTEKEN_CONFLICT',
  'GEEN_DATUM',
  'TRANSACTIE_AL_GEKOPPELD',
] as const
export type BrandstofReviewReden = (typeof BRANDSTOF_REVIEW_REDEN)[number]

export const BRANDSTOF_REVIEW_REDEN_LABELS: Record<BrandstofReviewReden, string> = {
  ONBEKENDE_PAS: 'Tankpas staat niet in het register',
  PAS_ZONDER_VOERTUIG: 'Tankpas had op de transactiedatum geen voertuig',
  VOERTUIG_ZONDER_BERIJDER: 'Voertuig had op de transactiedatum geen berijder',
  BERIJDER_AMBIGU: 'Meerdere berijders op de transactiedatum',
  KENTEKEN_ONBEKEND: 'Kenteken staat niet in het register',
  GEEN_PAS_OF_KENTEKEN: 'Geen tankpas en geen kenteken op de regel',
  PAS_KENTEKEN_CONFLICT: 'Tankpas en kenteken wijzen naar verschillende voertuigen',
  GEEN_DATUM: 'Geen leesbare transactiedatum',
  TRANSACTIE_AL_GEKOPPELD: 'Deze tankbeurt is al aan een andere factuurregel gekoppeld',
}

export const BRANDSTOF_KOPPELWIJZE = [
  'BESTAANDE_TRANSACTIE',
  'NIEUWE_TRANSACTIE',
  'ZONDER_TRANSACTIE',
] as const
export type BrandstofKoppelwijze = (typeof BRANDSTOF_KOPPELWIJZE)[number]

export const BRANDSTOF_KOPPELWIJZE_LABELS: Record<BrandstofKoppelwijze, string> = {
  BESTAANDE_TRANSACTIE: 'Gekoppeld aan bestaande tankbeurt',
  NIEUWE_TRANSACTIE: 'Tankbeurt aangemaakt uit de factuur',
  ZONDER_TRANSACTIE: 'Op kenteken (geen tankpas bekend)',
}

/** Herkomst van de voertuigkoppeling van een regel (provenance voor de UI). */
export const BRANDSTOF_REGISTER_BRON = ['HISTORIE', 'REGISTER_ACTUEEL', 'KENTEKEN'] as const
export type BrandstofRegisterBron = (typeof BRANDSTOF_REGISTER_BRON)[number]

/** Uitkomst van de (pure) koppeling van één factuurregel. */
export interface BrandstofKoppelUitkomst {
  status: 'GEKOPPELD' | 'REVIEW'
  reviewReden: BrandstofReviewReden | null
  toelichting: string | null
  fuelCardId: string | null
  voertuigId: string | null
  employeeId: string | null
  poolgebruik: boolean
  registerBron: BrandstofRegisterBron | null
}

/** Eén regel zoals de extractie (CSV of PDF-AI) hem aanlevert, vóór de koppeling. */
export interface BrandstofSpecificatieRegel {
  tankpasnummer: string | null
  /** ISO jjjj-mm-dd. */
  datum: string | null
  /** HH:mm (24-uurs) of null. */
  tijd: string | null
  station: string | null
  product: string | null
  kostensoort: BrandstofKostensoort
  hoeveelheid: number | null
  eenheid: string | null
  bedragExclBtw: number
  btwPercentage: number | null
  btwBedrag: number
  bedragInclBtw: number
  kenteken: string | null
  kmStand: number | null
}

export interface BrandstofKoppelingDTO {
  koppelwijze: BrandstofKoppelwijze
  fuelCardTransactionId: string | null
  gestorneerd: boolean
}

export interface BrandstofFactuurRegelDTO {
  id: string
  volgorde: number
  tankpasnummer: string | null
  /** ISO-8601 (UTC); de UI formatteert in Europe/Amsterdam. */
  transactieOp: string | null
  tijdBekend: boolean
  station: string | null
  product: string | null
  kostensoort: BrandstofKostensoort
  kostensoortBron: string
  hoeveelheid: number | null
  eenheid: string | null
  bedragExclBtw: number
  btwPercentage: number | null
  btwBedrag: number
  bedragInclBtw: number
  kenteken: string | null
  kmStand: number | null
  isPrive: boolean
  status: BrandstofRegelStatus
  reviewReden: BrandstofReviewReden | null
  reviewToelichting: string | null
  fuelCardId: string | null
  voertuigId: string | null
  voertuigKenteken: string | null
  /**
   * Berijder van de tankbeurt. Alleen gevuld voor wie de berijder mag zien (INKOOP.canEdit
   * of een kostprijs-rol, nooit de accountant): station + tijdstip per medewerker is een
   * locatiegegeven dat voor factuurcontrole niet nodig is (AVG, dataminimalisatie). Anders
   * altijd `null`, ook als er wel een berijder is (zie `berijderZichtbaar` op het detail).
   */
  employeeId: string | null
  employeeNaam: string | null
  poolgebruik: boolean
  handmatig: boolean
  handmatigReden: string | null
  herkomst: string
  koppeling: BrandstofKoppelingDTO | null
}

export interface BrandstofFactuurDTO {
  id: string
  inkoopFactuurId: string | null
  inkoopFactuurNr: string | null
  leverancierNaam: string | null
  factuurnummer: string | null
  factuurdatum: string | null
  bron: string
  status: BrandstofFactuurStatus
  factuurTotaalExclBtw: number | null
  factuurBtw: number | null
  factuurTotaalInclBtw: number | null
  regelsomExclBtw: number
  regelsomBtw: number
  regelsomInclBtw: number
  verschilInclBtw: number | null
  totaalSluit: boolean
  aantalRegels: number
  aantalGekoppeld: number
  aantalReview: number
  markeringen: string[]
  extractieZekerheid: number | null
  bronBestandsnaam: string | null
  verwerktOp: string | null
  verworpenReden: string | null
  createdAt: string
}

export interface BrandstofFactuurDetailDTO extends BrandstofFactuurDTO {
  regels: BrandstofFactuurRegelDTO[]
  /** Mag de ingelogde gebruiker regels koppelen/corrigeren (INKOOP.canEdit, geen accountant). */
  magBewerken: boolean
  /**
   * Ziet de ingelogde gebruiker de berijder per regel (INKOOP.canEdit of een kostprijs-rol,
   * nooit de accountant)? Zo niet, dan zijn `employeeId`/`employeeNaam` op elke regel `null`
   * en toont de UI "afgeschermd" in plaats van "geen berijder".
   */
  berijderZichtbaar: boolean
}

export interface BrandstofReviewRegelDTO extends BrandstofFactuurRegelDTO {
  brandstofFactuurId: string
  factuurnummer: string | null
  factuurStatus: BrandstofFactuurStatus
}

/** Paneel op de inkoopfactuur-review (eigen paneel naast de bestaande review-UI). */
export interface InkoopFactuurBrandstofPaneelDTO {
  inkoopFactuurId: string
  isBrandstofLeverancier: boolean
  heeftPdfBijlage: boolean
  brandstofFactuur: BrandstofFactuurDTO | null
  magBewerken: boolean
}

export interface TankpasVoertuigPeriodeDTO {
  id: string
  voertuigId: string | null
  voertuigKenteken: string | null
  vanaf: string
  tot: string | null
  bron: string
  reden: string | null
}

/** Specificatie inlezen op een inkoopfactuur: de factuur-PDF (AI) of een CSV/Excel-specificatie. */
export const brandstofSpecificatieInlezenSchema = z
  .object({
    bron: z.enum(['FACTUUR_PDF', 'SPECIFICATIE']),
    specificatieBase64: z.string().optional(),
    bestandsnaam: z.string().trim().max(255).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.bron === 'SPECIFICATIE' && !(v.specificatieBase64 && v.specificatieBase64.length > 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['specificatieBase64'],
        message: 'Kies een CSV- of Excel-specificatie van MKB.',
      })
    }
  })
export type BrandstofSpecificatieInlezenInput = z.infer<typeof brandstofSpecificatieInlezenSchema>

/** Handmatige upload zonder inkoopfactuur (specificatie en/of PDF + factuurtotaal). */
export const brandstofFactuurUploadSchema = z
  .object({
    pdfBase64: z.string().optional(),
    specificatieBase64: z.string().optional(),
    bestandsnaam: z.string().trim().max(255).optional(),
    factuurnummer: z.string().trim().max(100).optional(),
    factuurdatum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd.')
      .optional(),
    factuurTotaalInclBtw: z.number().finite().optional(),
  })
  .superRefine((v, ctx) => {
    const heeftPdf = !!v.pdfBase64 && v.pdfBase64.length > 0
    const heeftSpec = !!v.specificatieBase64 && v.specificatieBase64.length > 0
    if (!heeftPdf && !heeftSpec) {
      ctx.addIssue({
        code: 'custom',
        path: ['specificatieBase64'],
        message: 'Lever een CSV/Excel-specificatie en/of de PDF-factuur aan.',
      })
    }
  })
export type BrandstofFactuurUploadInput = z.infer<typeof brandstofFactuurUploadSchema>

const redenSchema = z.string().trim().min(3, 'Geef een reden van minstens 3 tekens.').max(500)

/**
 * Handmatige koppeling/correctie van één regel. Koppelvelden mogen altijd (ook na
 * verwerken); bedragen alleen zolang de factuur TE_CONTROLEREN is (server-side
 * afgedwongen). Een reden is verplicht bij "algemeen", privé en bedragwijzigingen.
 */
export const brandstofRegelPatchSchema = z
  .object({
    fuelCardId: idParam('tankpas').nullable().optional(),
    voertuigId: idParam('voertuig').nullable().optional(),
    employeeId: idParam('medewerker').nullable().optional(),
    poolgebruik: z.boolean().optional(),
    algemeen: z.boolean().optional(),
    kostensoort: z.enum(BRANDSTOF_KOSTENSOORT).optional(),
    isPrive: z.boolean().optional(),
    bedragExclBtw: z.number().finite().optional(),
    btwBedrag: z.number().finite().optional(),
    reden: z.string().trim().max(500).optional(),
  })
  .superRefine((v, ctx) => {
    const redenVerplicht =
      v.algemeen === true ||
      v.isPrive !== undefined ||
      v.bedragExclBtw !== undefined ||
      v.btwBedrag !== undefined
    if (redenVerplicht && !(v.reden && v.reden.trim().length >= 3)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reden'],
        message: 'Geef een reden van minstens 3 tekens.',
      })
    }
    if (v.poolgebruik === true && v.employeeId) {
      ctx.addIssue({
        code: 'custom',
        path: ['employeeId'],
        message: 'Bij poolgebruik blijft de gebruiker leeg.',
      })
    }
  })
export type BrandstofRegelPatchInput = z.infer<typeof brandstofRegelPatchSchema>

/** Een ontbrekende regel toevoegen tijdens de controle (bv. kaartkosten). */
export const brandstofRegelToevoegenSchema = z.object({
  tankpasnummer: z.string().trim().max(60).nullable().optional(),
  kenteken: z.string().trim().max(20).nullable().optional(),
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd.'),
  kostensoort: z.enum(BRANDSTOF_KOSTENSOORT),
  product: z.string().trim().max(200).nullable().optional(),
  hoeveelheid: z.number().finite().nullable().optional(),
  bedragExclBtw: z.number().finite(),
  btwBedrag: z.number().finite(),
  reden: redenSchema,
})
export type BrandstofRegelToevoegenInput = z.infer<typeof brandstofRegelToevoegenSchema>

export const brandstofRedenSchema = z.object({ reden: redenSchema })
export type BrandstofRedenInput = z.infer<typeof brandstofRedenSchema>

/** Handmatige correctie van de tankpas-historie (vanaf een datum hoort de pas bij ...). */
export const tankpasVoertuigPeriodeSchema = z.object({
  voertuigId: idParam('voertuig').nullable(),
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd.'),
  reden: redenSchema,
})
export type TankpasVoertuigPeriodeInput = z.infer<typeof tankpasVoertuigPeriodeSchema>

export const poolVoertuigSchema = z.object({
  pool: z.boolean(),
  /** Vanaf welke NL-dag (jjjj-mm-dd); weglaten = vandaag. */
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd.').optional(),
  reden: redenSchema,
})
export type PoolVoertuigInput = z.infer<typeof poolVoertuigSchema>

/** Filter voor de brandstoffacturen-lijst en de review-werkvoorraad. */
export const brandstofLijstQuerySchema = z.object({
  status: z.enum(BRANDSTOF_FACTUUR_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type BrandstofLijstQuery = z.infer<typeof brandstofLijstQuerySchema>

/** Namen waaraan een brandstofleverancier (Client) herkend wordt (genormaliseerd bevat). */
// 'mkb ondernemers' (batch-62 integrator-nazorg): de tankpasleverancier staat op prod als relatie
// "MKB Ondernemers BV" (de juridische naam achter MKB Brandstof; al zijn inkoopfacturen zijn
// tankpasfacturen). Zonder dit patroon herkende niets zijn facturen: geen paneel, geen inlezen.
export const BRANDSTOF_LEVERANCIER_PATRONEN = ['mkb brandstof', 'mkbbrandstof', 'mkb-brandstof', 'mkb ondernemers'] as const

// ─── Loonstrookcomponenten (FO-461) ──────────────────────────────────────────

export const KOSTPRIJS_LOON_CATEGORIE = [
  'BASISLOON',
  'TOESLAG_PLOEG',
  'TOESLAG_OVERWERK',
  'TOESLAG_WEEKEND_FEESTDAG',
  'REISUREN',
  'BIJTELLING',
  'EIGEN_BIJDRAGE',
  'VERGOEDING_ONBELAST',
  'INHOUDING',
  'WERKGEVERSLASTEN',
  'OVERIG',
  // Zichtbaar, geen kost: de uitbetaling van vakantiegeld (dat maandelijks al via de
  // werkgeverslasten gereserveerd wordt) of een informatieregel op de strook.
  'INFORMATIEF',
] as const
export type KostprijsLoonCategorie = (typeof KOSTPRIJS_LOON_CATEGORIE)[number]

export const KOSTPRIJS_LOON_CATEGORIE_LABELS: Record<KostprijsLoonCategorie, string> = {
  BASISLOON: 'Basisloon',
  TOESLAG_PLOEG: 'Ploegentoeslag',
  TOESLAG_OVERWERK: 'Overwerktoeslag',
  TOESLAG_WEEKEND_FEESTDAG: 'Weekend- en feestdagtoeslag',
  REISUREN: 'Reisuren',
  BIJTELLING: 'Bijtelling',
  EIGEN_BIJDRAGE: 'Eigen bijdrage',
  VERGOEDING_ONBELAST: 'Onbelaste vergoeding',
  INHOUDING: 'Inhouding',
  WERKGEVERSLASTEN: 'Werkgeverslasten',
  OVERIG: 'Overig',
  INFORMATIEF: 'Informatief (telt niet)',
}

/** Bucket voor componenten zonder geldige mapping: zichtbaar, nooit stil in OVERIG. */
export const NOG_TE_CLASSIFICEREN = 'NOG_TE_CLASSIFICEREN' as const
export type LoonkostBucket = KostprijsLoonCategorie | typeof NOG_TE_CLASSIFICEREN

export const LOONKOST_BUCKETS: readonly LoonkostBucket[] = [
  ...KOSTPRIJS_LOON_CATEGORIE,
  NOG_TE_CLASSIFICEREN,
]

export const LOONKOST_BUCKET_LABELS: Record<LoonkostBucket, string> = {
  ...KOSTPRIJS_LOON_CATEGORIE_LABELS,
  NOG_TE_CLASSIFICEREN: 'Nog te classificeren',
}

/** Categorieen die als werkgeverskost in de kostprijs tellen. */
export const KOSTPRIJS_TELLENDE_LOON_CATEGORIEEN: readonly KostprijsLoonCategorie[] = [
  'BASISLOON',
  'TOESLAG_PLOEG',
  'TOESLAG_OVERWERK',
  'TOESLAG_WEEKEND_FEESTDAG',
  'REISUREN',
  'VERGOEDING_ONBELAST',
  'WERKGEVERSLASTEN',
  'OVERIG',
]

/** Categorieen die de premie-grondslag (bruto loon) vormen voor afgeleide werkgeverslasten. */
export const KOSTPRIJS_BRUTO_LOON_CATEGORIEEN: readonly KostprijsLoonCategorie[] = [
  'BASISLOON',
  'TOESLAG_PLOEG',
  'TOESLAG_OVERWERK',
  'TOESLAG_WEEKEND_FEESTDAG',
  'REISUREN',
  'OVERIG',
]

/** Zichtbaar, maar nooit een kostprijscomponent (fiscaal bij de medewerker / inhouding). */
export const KOSTPRIJS_NIET_TELLENDE_LOON_CATEGORIEEN: readonly KostprijsLoonCategorie[] = [
  'BIJTELLING',
  'EIGEN_BIJDRAGE',
  'INHOUDING',
  'INFORMATIEF',
]

export const LOON_COMPONENT_MATCH_WIJZE = ['EXACT', 'BEVAT'] as const
export type LoonComponentMatchWijze = (typeof LOON_COMPONENT_MATCH_WIJZE)[number]

export const LOON_COMPONENT_MATCH_WIJZE_LABELS: Record<LoonComponentMatchWijze, string> = {
  EXACT: 'Exact deze omschrijving',
  BEVAT: 'Omschrijving bevat',
}

/** Eén mapping-versie zoals de pure resolver hem nodig heeft. */
export interface LoonMappingKandidaat {
  id: string
  patroon: string
  matchWijze: LoonComponentMatchWijze
  categorie: KostprijsLoonCategorie
  /** jjjjmm, inclusief. */
  geldigVanaf: number
  /** jjjjmm, inclusief; null = open. */
  geldigTot: number | null
}

/**
 * Standaard-mapping (seed). Bewust BEVAT-patronen op de gangbare NMBRS/Metaal en
 * Techniek-omschrijvingen; alles wat hier niet onder valt komt in de te-mappen-lijst.
 * Beheerbaar (SALARIS/ADMIN): een wijziging is een nieuwe, versie-vaste mapping.
 */
export const LOON_COMPONENT_MAPPING_SEED: ReadonlyArray<{
  patroon: string
  categorie: KostprijsLoonCategorie
}> = [
  { patroon: 'salaris', categorie: 'BASISLOON' },
  { patroon: 'maandloon', categorie: 'BASISLOON' },
  { patroon: 'uurloon', categorie: 'BASISLOON' },
  { patroon: 'basisloon', categorie: 'BASISLOON' },
  { patroon: 'gewerkte uren', categorie: 'BASISLOON' },
  { patroon: 'normale uren', categorie: 'BASISLOON' },
  { patroon: 'ploegentoeslag', categorie: 'TOESLAG_PLOEG' },
  { patroon: 'ploegtoeslag', categorie: 'TOESLAG_PLOEG' },
  { patroon: 'ploegendienst', categorie: 'TOESLAG_PLOEG' },
  { patroon: 'onregelmatigheid', categorie: 'TOESLAG_PLOEG' },
  { patroon: 'overwerk', categorie: 'TOESLAG_OVERWERK' },
  { patroon: 'overuren', categorie: 'TOESLAG_OVERWERK' },
  { patroon: 'meeruren', categorie: 'TOESLAG_OVERWERK' },
  { patroon: 'zaterdag', categorie: 'TOESLAG_WEEKEND_FEESTDAG' },
  { patroon: 'zondag', categorie: 'TOESLAG_WEEKEND_FEESTDAG' },
  { patroon: 'feestdag', categorie: 'TOESLAG_WEEKEND_FEESTDAG' },
  { patroon: 'weekend', categorie: 'TOESLAG_WEEKEND_FEESTDAG' },
  { patroon: 'reisuren', categorie: 'REISUREN' },
  { patroon: 'reistijd', categorie: 'REISUREN' },
  { patroon: 'bijtelling', categorie: 'BIJTELLING' },
  { patroon: 'eigen bijdrage', categorie: 'EIGEN_BIJDRAGE' },
  { patroon: 'reiskosten', categorie: 'VERGOEDING_ONBELAST' },
  { patroon: 'kilometervergoeding', categorie: 'VERGOEDING_ONBELAST' },
  { patroon: 'km vergoeding', categorie: 'VERGOEDING_ONBELAST' },
  { patroon: 'km-vergoeding', categorie: 'VERGOEDING_ONBELAST' },
  { patroon: 'onkostenvergoeding', categorie: 'VERGOEDING_ONBELAST' },
  { patroon: 'thuiswerkvergoeding', categorie: 'VERGOEDING_ONBELAST' },
  // Vakantiegeld wordt maandelijks gereserveerd (vakantiegeld-% in de afgeleide
  // werkgeverslasten); de uitbetaling en de reserveringsregel tellen daarom niet nog eens.
  { patroon: 'reservering vakantiegeld', categorie: 'INFORMATIEF' },
  { patroon: 'werkgeverslasten', categorie: 'WERKGEVERSLASTEN' },
  { patroon: 'pensioen werkgever', categorie: 'WERKGEVERSLASTEN' },
  { patroon: 'pensioenpremie werkgever', categorie: 'WERKGEVERSLASTEN' },
  { patroon: 'vakantiegeld', categorie: 'INFORMATIEF' },
  { patroon: 'eindejaarsuitkering', categorie: 'OVERIG' },
  { patroon: '13e maand', categorie: 'OVERIG' },
  { patroon: 'provisie', categorie: 'OVERIG' },
  { patroon: 'gratificatie', categorie: 'OVERIG' },
  { patroon: 'bonus', categorie: 'OVERIG' },
  { patroon: 'loonheffing', categorie: 'INHOUDING' },
  { patroon: 'pensioenpremie', categorie: 'INHOUDING' },
]

/** Seed-mappings gelden vanaf de eerste periode die de app kent. */
export const LOON_COMPONENT_MAPPING_SEED_VANAF = 200001

export const LOON_TE_MAPPEN_STATUS = ['OPEN', 'GEMAPT', 'OPGELOST'] as const
export type LoonTeMappenStatus = (typeof LOON_TE_MAPPEN_STATUS)[number]

export const LOON_SUGGESTIE_BRON = ['HISTORIE', 'TREFWOORD', 'EXTRACTIE'] as const
export type LoonSuggestieBron = (typeof LOON_SUGGESTIE_BRON)[number]

export interface LoonComponentMappingDTO {
  id: string
  patroon: string
  matchWijze: LoonComponentMatchWijze
  categorie: KostprijsLoonCategorie
  geldigVanaf: number
  geldigTot: number | null
  versie: number
  bron: string
  toelichting: string | null
  /** Geldt deze versie in de huidige periode. */
  actueel: boolean
  createdAt: string
}

export interface LoonComponentTeMappenDTO {
  id: string
  patroon: string
  voorbeeldOmschrijving: string
  aiCategorie: string | null
  suggestieCategorie: KostprijsLoonCategorie | null
  suggestieBron: LoonSuggestieBron | null
  aantalComponenten: number
  aantalMedewerkers: number
  /** Som over alle stroken; null zonder salaris-inzage. */
  totaalBedrag: number | null
  eerstePeriode: number
  laatstePeriode: number
  status: LoonTeMappenStatus
}

export interface LoonComponentMappingOverzichtDTO {
  mappingen: LoonComponentMappingDTO[]
  teMappen: LoonComponentTeMappenDTO[]
  huidigePeriode: number
  /** SALARIS/ADMIN mag wijzigen (server-side afgedwongen). */
  magBeheren: boolean
}

/** Een mapping maken of wijzigen (een wijziging = nieuwe versie vanaf de ingangsperiode). */
export const loonComponentMappingOpslaanSchema = z
  .object({
    patroon: z.string().trim().min(2, 'Geef minstens 2 tekens.').max(120),
    matchWijze: z.enum(LOON_COMPONENT_MATCH_WIJZE).default('EXACT'),
    categorie: z.enum(KOSTPRIJS_LOON_CATEGORIE),
    /** Ingang (jjjj-mm); weglaten = de huidige periode (vooruit). */
    ingangsperiode: periodeLabelSchema.optional(),
    /** Bewust terugwerkend herrekenen (alleen na het impact-rapport). */
    terugwerkend: z.boolean().optional(),
    reden: z.string().trim().max(500).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.terugwerkend && !(v.reden && v.reden.trim().length >= 3)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reden'],
        message: 'Geef bij terugwerkend herrekenen een reden van minstens 3 tekens.',
      })
    }
  })
export type LoonComponentMappingOpslaanInput = z.infer<typeof loonComponentMappingOpslaanSchema>

export const loonComponentMappingDeactiverenSchema = z.object({
  ingangsperiode: periodeLabelSchema.optional(),
  terugwerkend: z.boolean().optional(),
  reden: redenSchema,
})
export type LoonComponentMappingDeactiverenInput = z.infer<
  typeof loonComponentMappingDeactiverenSchema
>

/** Alleen het impact-rapport van een voorgenomen wijziging (categorie null = deactiveren). */
export const loonComponentMappingRapportSchema = z.object({
  patroon: z.string().trim().min(2, 'Geef minstens 2 tekens.').max(120),
  matchWijze: z.enum(LOON_COMPONENT_MATCH_WIJZE).default('EXACT'),
  categorie: z.enum(KOSTPRIJS_LOON_CATEGORIE).nullable(),
  ingangsperiode: periodeLabelSchema.optional(),
})
export type LoonComponentMappingRapportInput = z.infer<typeof loonComponentMappingRapportSchema>

export const loonComponentTeMappenToewijzenSchema = z
  .object({
    categorie: z.enum(KOSTPRIJS_LOON_CATEGORIE),
    /** Bevestiging na het impact-rapport (409) wanneer een bestaande classificatie verschuift. */
    terugwerkend: z.boolean().optional(),
    reden: z.string().trim().max(500).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.terugwerkend && !(v.reden && v.reden.trim().length >= 3)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reden'],
        message: 'Geef bij terugwerkend herrekenen een reden van minstens 3 tekens.',
      })
    }
  })
export type LoonComponentTeMappenToewijzenInput = z.infer<
  typeof loonComponentTeMappenToewijzenSchema
>

export interface LoonMappingRapportRegel {
  employeeId: string
  employeeNaam: string
  periode: number
  categorie: LoonkostBucket
  oud: number
  nieuw: number
}

export interface LoonMappingRapportDTO {
  ingangsperiode: number
  huidigePeriode: number
  /** True = er verschuift een bedrag tussen twee échte categorieen in een verleden periode. */
  wijzigtBestaandeClassificatie: boolean
  aantalPerioden: number
  aantalMedewerkers: number
  regels: LoonMappingRapportRegel[]
}

/** Foutcode wanneer een terugwerkende wijziging eerst het rapport vereist (409). */
export const LOON_MAPPING_RAPPORT_VEREIST_CODE = 'TERUGWERKEND_RAPPORT_VEREIST'

// ─── Bijtelling + eigen bijdrage (FO-461 §B) ─────────────────────────────────

export const BIJTELLING_TOEREKENING_STATUS = ['AUTO', 'HANDMATIG', 'TWIJFEL', 'GEEN_VOERTUIG'] as const
export type BijtellingToerekeningStatus = (typeof BIJTELLING_TOEREKENING_STATUS)[number]

export const BIJTELLING_TOEREKENING_STATUS_LABELS: Record<BijtellingToerekeningStatus, string> = {
  AUTO: 'Automatisch (berijder-historie)',
  HANDMATIG: 'Handmatig verdeeld',
  TWIJFEL: 'Handmatige verdeling nodig',
  GEEN_VOERTUIG: 'Geen voertuig in de periode',
}

export interface BijtellingToerekeningDTO {
  voertuigId: string
  kenteken: string | null
  vanaf: string
  tot: string
  dagen: number
  aandeel: number
  bijtellingDeel: number
  eigenBijdrageDeel: number
  bron: string
}

export interface BijtellingRegistratieDTO {
  id: string
  employeeId: string
  periode: number
  bijtellingBedrag: number
  eigenBijdrageBedrag: number
  nettoBijtelling: number
  bron: string
  toerekeningStatus: BijtellingToerekeningStatus
  twijfelReden: string | null
  toerekeningen: BijtellingToerekeningDTO[]
}

export const bijtellingToerekeningHandmatigSchema = z
  .object({
    verdeling: z
      .array(
        z.object({
          voertuigId: idParam('voertuig'),
          aandeelPct: z.number().min(0).max(100),
        }),
      )
      .min(1, 'Kies minstens één voertuig.'),
    reden: redenSchema,
  })
  .superRefine((v, ctx) => {
    const som = v.verdeling.reduce((s, d) => s + d.aandeelPct, 0)
    if (Math.abs(som - 100) > 0.01) {
      ctx.addIssue({
        code: 'custom',
        path: ['verdeling'],
        message: `De verdeling moet samen 100% zijn (nu ${Math.round(som * 100) / 100}%).`,
      })
    }
    const ids = v.verdeling.map((d) => d.voertuigId)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: 'custom', path: ['verdeling'], message: 'Elk voertuig maar één keer.' })
    }
  })
export type BijtellingToerekeningHandmatigInput = z.infer<
  typeof bijtellingToerekeningHandmatigSchema
>

// ─── Werkelijke kostprijs (FO-460 §B + FO-461 §C) ────────────────────────────

export const WERKGEVERSLASTEN_BRON = ['STROOK', 'AFGELEID'] as const
export type WerkgeverslastenBron = (typeof WERKGEVERSLASTEN_BRON)[number]

/** Volledigheidsmarkeringen op een periode (tekst in de UI via de labels). */
export const KOSTPRIJS_MARKERING = [
  'GEEN_LOONSTROOK',
  'NOG_TE_CLASSIFICEREN',
  'WERKGEVERSLASTEN_AFGELEID',
  'GEEN_UREN',
  'ONDERHOUD_NORM',
  'BIJKOMEND_NORM',
] as const
export type KostprijsMarkering = (typeof KOSTPRIJS_MARKERING)[number]

export const KOSTPRIJS_MARKERING_LABELS: Record<KostprijsMarkering, string> = {
  GEEN_LOONSTROOK: 'Geen loonstrook in deze periode - loonkost uit de norm',
  NOG_TE_CLASSIFICEREN: 'Er staan looncomponenten in de te-mappen-lijst; de kostprijs is onvolledig',
  WERKGEVERSLASTEN_AFGELEID: 'Werkgeverslasten staan niet (volledig) op de strook - afgeleid uit de parameters',
  GEEN_UREN: 'Geen geregistreerde productieve uren - geen kostprijs per uur',
  ONDERHOUD_NORM: 'Geen werkelijke onderhoudskosten bekend - norm getoond',
  BIJKOMEND_NORM: 'Telefoon, gereedschap en kleding volgen de norm',
}

export interface KostprijsWerkelijkBedragen {
  loonkost: number | null
  werkgeverslasten: number
  werkgeverslastenBron: WerkgeverslastenBron
  opslagen: number
  bijkomend: number
  /** Strookbedrag zonder categorie; null zonder salaris-inzage. */
  nogTeClassificeren: number | null
  brandstof: number
  brandstofPrive: number
  voertuigVast: number
  onderhoud: number
  schade: number
  totaal: number
  productieveUren: number | null
  reisuren: number | null
  kostprijsPerProductiefUur: number | null
}

export interface KostprijsNormBedragen {
  loonkost: number
  werkgeverslasten: number
  opslagen: number
  bijkomend: number
  brandstof: number
  voertuigVast: number
  onderhoud: number
  totaal: number
  kostprijsPerUur: number | null
}

export interface KostprijsLoonCategorieBedrag {
  categorie: LoonkostBucket
  bedrag: number
  aantal: number | null
}

export interface KostprijsBrandstofVoertuigDeel {
  voertuigId: string
  kenteken: string | null
  bedrag: number
}

export interface KostprijsWerkelijkPeriodeDTO {
  periode: number
  periodeLabel: string
  werkelijk: KostprijsWerkelijkBedragen
  /** Per categorie (null wanneer de loondetails afgeschermd zijn). */
  loonPerCategorie: KostprijsLoonCategorieBedrag[] | null
  brandstofPerKostensoort: Record<BrandstofKostensoort, number>
  brandstofPerVoertuig: KostprijsBrandstofVoertuigDeel[]
  norm: KostprijsNormBedragen
  verschilTotaal: number
  /** Null wanneer bijtelling afgeschermd is of er geen registratie is. */
  bijtelling: BijtellingRegistratieDTO | null
  markeringen: KostprijsMarkering[]
  onderhoudSchadeBron: string
  berekendOp: string | null
}

export interface KostprijsWerkelijkMedewerkerDTO {
  employeeId: string
  employeeNaam: string
  van: string
  tot: string
  /** True = de gebruiker ziet totalen maar geen loonopbouw per categorie (geen salaris-inzage). */
  loonDetailAfgeschermd: boolean
  perioden: KostprijsWerkelijkPeriodeDTO[]
  samenvatting: {
    maandenMetData: number
    kostprijsPerProductiefUur: number | null
    normKostprijsPerUur: number | null
    /** WERKELIJK zodra er genoeg volledige maanden zijn; anders NORM. */
    kostprijsBron: 'WERKELIJK' | 'NORM'
  }
}

export interface KostprijsVoertuigBerijderDeel {
  employeeId: string
  naam: string
  vanaf: string
  tot: string | null
  brandstof: number
  bijtellingDeel: number | null
  eigenBijdrageDeel: number | null
}

export interface KostprijsWerkelijkVoertuigPeriodeDTO {
  periode: number
  periodeLabel: string
  brandstofTotaal: number
  prive: number
  liters: number
  perKostensoort: Record<BrandstofKostensoort, { bedrag: number; hoeveelheid: number; aantal: number }>
  kmVerreden: number | null
  verbruikPer100Km: number | null
  kostenPerKm: number | null
  onderhoud: number
  schade: number
  onderhoudSchadeBron: string
  bijtelling: number | null
  eigenBijdrage: number | null
  aantalRegels: number
  aantalPoolgebruik: number
  aantalZonderFactuur: number
  berijders: KostprijsVoertuigBerijderDeel[]
}

export interface KostprijsWerkelijkVoertuigDTO {
  voertuigId: string
  kenteken: string
  isPoolauto: boolean
  van: string
  tot: string
  /** True = bijtelling/eigen bijdrage afgeschermd (alleen salaris-inzage-rollen). */
  bijtellingAfgeschermd: boolean
  perioden: KostprijsWerkelijkVoertuigPeriodeDTO[]
  totalen: {
    brandstofTotaal: number
    liters: number
    kmVerreden: number | null
    verbruikPer100Km: number | null
    kostenPerKm: number | null
    onderhoud: number
    schade: number
  }
}

/** Herberekening/backfill (integrator): dry-run levert het rapport, apply schrijft. */
export const kostprijsHerberekenSchema = z
  .object({
    van: periodeLabelSchema,
    tot: periodeLabelSchema,
    dryRun: z.boolean().default(true),
  })
  .refine((v) => v.van <= v.tot, { path: ['tot'], message: 'De eindperiode ligt vóór de beginperiode.' })
export type KostprijsHerberekenInput = z.infer<typeof kostprijsHerberekenSchema>

/**
 * Maximaal aantal maanden per expliciete herberekening van één medewerker (de uren
 * komen uit de payroll-aggregatie per maand; langere reeksen = de nachtcheck of de
 * integrator-herberekening).
 */
export const KOSTPRIJS_MAX_HERBEREKEN_MAANDEN = 3

/** Expliciete herberekening van één medewerker (knop in de kostprijs-tab). */
export const kostprijsMedewerkerHerberekenSchema = z
  .object({ van: periodeLabelSchema, tot: periodeLabelSchema })
  .superRefine((v, ctx) => {
    if (v.van > v.tot) {
      ctx.addIssue({ code: 'custom', path: ['tot'], message: 'De eindperiode ligt vóór de beginperiode.' })
      return
    }
    const [vj, vm] = v.van.split('-').map(Number) as [number, number]
    const [tj, tm] = v.tot.split('-').map(Number) as [number, number]
    if ((tj - vj) * 12 + (tm - vm) + 1 > KOSTPRIJS_MAX_HERBEREKEN_MAANDEN) {
      ctx.addIssue({
        code: 'custom',
        path: ['tot'],
        message: `Herbereken maximaal ${KOSTPRIJS_MAX_HERBEREKEN_MAANDEN} maanden tegelijk.`,
      })
    }
  })
export type KostprijsMedewerkerHerberekenInput = z.infer<typeof kostprijsMedewerkerHerberekenSchema>

export interface KostprijsHerberekenAfwijking {
  soort: 'LOONKOST' | 'MEDEWERKER' | 'VOERTUIG' | 'BRANDSTOF_FACTUUR' | 'KOPPELING'
  id: string
  periode: number | null
  toelichting: string
}

export interface KostprijsHerberekenRapportDTO {
  van: number
  tot: number
  dryRun: boolean
  aantalLoonPerioden: number
  aantalMedewerkerPerioden: number
  aantalVoertuigPerioden: number
  aantalGewijzigd: number
  /** Brandstoffacturen met regels in het bereik (gesynchroniseerd bij apply). */
  aantalBrandstofFacturen: number
  /** Brandstof-inkoopfacturen in het bereik zonder ingelezen specificatie. */
  aantalNietIngelezen: number
  /** Bij apply met inlezen: zoveel specificaties ingelezen. */
  aantalIngelezen: number
  afwijkingen: KostprijsHerberekenAfwijking[]
}

export interface KostprijsConsistentieRunDTO {
  id: string
  gestartOp: string
  afgerondOp: string | null
  vanPeriode: number
  totPeriode: number
  uitkomst: string
  aantalGecontroleerd: number
  aantalAfwijkingen: number
  aantalHersteld: number
}

/** Standaardwaarden van de instellingen (overschrijfbaar via KostprijsWerkelijkInstelling). */
export const KOSTPRIJS_WERKELIJK_DEFAULTS = {
  /** Signaal "ongekoppelde brandstofregels" na zoveel dagen. */
  ongekoppeldSignaalDagen: 14,
  /** Verbruik afwijkend bij >= factor x (of <= 1/factor x) het voertuiggemiddelde. */
  verbruikAfwijkingFactor: 2,
  /** Minimaal aantal maanden historie voor een voertuiggemiddelde. */
  verbruikMinHistorieMaanden: 2,
  /** Dedupe: zelfde tankpas, NL-dag +/- zoveel dagen. */
  dedupeDagTolerantie: 1,
  /** Dedupe: bedrag incl. btw +/- zoveel euro. */
  dedupeBedragTolerantie: 0.02,
  /** Nachtelijke consistentiecheck: zoveel maanden terug (incl. de huidige). */
  consistentieMaanden: 3,
  /** Minimaal aantal volledige maanden voordat de werkelijke kostprijs leidend is. */
  werkelijkLeidendMinMaanden: 3,
} as const
